import axios from "axios";
import API_BASE_URL from "../../../config/apiConfig";
import { logEndpointError } from "../debug/endpointLogger";

const API_URL = `${API_BASE_URL}/api/SpecialistReferrals`;

// ============================================================
// Servicio de Specialist Referrals
// Controller: SpecialistReferralsController
// Ruta base: /api/SpecialistReferrals
// Autenticación: JWT Bearer (todos los endpoints)
// Total funciones: 21
// ============================================================

const authHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

// ────────────────────────────────────────────────────────────
// GRUPO A: CRUD (6 funciones)
// ────────────────────────────────────────────────────────────

/**
 * Crea un nuevo Specialist Referral. Si el especialista viene del directorio,
 * incrementa su contador de uso. Si la razón de referral es nueva, la registra
 * automáticamente para autocomplete futuro.
 *
 * @param {Object} data - Datos del referral
 * @param {string} data.cipId - ID del paciente (CIP) — requerido
 * @param {string} [data.memberId] - Member ID del seguro
 * @param {number} data.urgencyLevelId - Nivel de urgencia: 7=Low, 8=Moderate, 9=High
 * @param {string|null} [data.icd10Codes] - DEPRECADO (Cambio 1). Enviar siempre `null`; usar `diagnoses`.
 * @param {string} [data.reasonForReferral] - Texto libre / autocomplete
 * @param {string} [data.hedisCodes] - Códigos HEDIS separados por coma
 * @param {boolean} data.hasRubiconBeenUsed - Si se usó Rubicon
 * @param {boolean} data.lab - Si requiere lab
 * @param {boolean} data.imaging - Si requiere imaging
 * @param {number} data.specialistTypeId - FK a TAB_SPECIALIST_TYPES
 * @param {number} [data.specialistId] - FK a TAB_SPECIALISTS_DIRECTORY (si viene del directorio)
 * @param {string} [data.specialistName] - Nombre desnormalizado del especialista
 * @param {string} [data.specialistNpi] - NPI desnormalizado
 * @param {string} [data.specialistPhone] - Teléfono desnormalizado
 * @param {string} [data.specialistFax] - Fax desnormalizado
 * @param {string} [data.specialistAddress] - Dirección desnormalizada
 * @param {number} [data.careManagerId] - Care Manager asignado
 * @param {number} data.pcpId - PCP del paciente
 * @param {number} data.locationId - Centro médico
 * @param {number} data.employeeStaffId - Empleado staff que registra
 * @param {number} data.referralStatusId - Estado del referral: 10-14
 * @param {number} data.followupStatusId - Estado de seguimiento: 15-17
 * @param {string} [data.followupNotes] - Notas de seguimiento
 * @param {string} [data.availityReferral] - Fecha de referral en Availity (YYYY-MM-DD)
 * @param {boolean} data.needsCareManager - Cambio 3. Si true, careManagerId es obligatorio
 * @param {number|null} [data.referralRequestId] - Cambio 4. Status group `specialist_referral_request`
 * @param {number|null} [data.medicalDirectorStatusId] - Cambio 5. Status group `medical_director_status`
 * @param {number|null} [data.medicalDirectorFollowupId] - Cambio 5. Status group `medical_director_followup`
 * @param {string|null} [data.directorReviewed] - Cambio 5. Fecha de revisión del director (YYYY-MM-DD)
 * @param {number|null} [data.medicalDirectorId] - Cambio 5. FK al catálogo de Medical Directors
 * @param {number|null} [data.patientCodeStatusId] - Cambio 7. Código CPT del paciente (status group `patient_code_status`): 100=New Patient, 101=Existing Patient. Opcional — null = sin código.
 * @param {Array<{codeDiagnosis: string, isPrimary: boolean}>} data.diagnoses - Cambio 1.
 *        Lista de diagnósticos ICD-10 (mínimo 1 primario). El backend la orquesta en transacción.
 *
 * Ejemplo de llamada:
 * POST /api/SpecialistReferrals
 * Body: {
 *   "cipId": "CIP000001", "urgencyLevelId": 7, "specialistTypeId": 1, "icd10Codes": null,
 *   "needsCareManager": true, "careManagerId": 27, "referralRequestId": 41,
 *   "diagnoses": [
 *     { "codeDiagnosis": "E11.9", "isPrimary": true },
 *     { "codeDiagnosis": "I10", "isPrimary": false }
 *   ], ...
 * }
 *
 * Respuesta exitosa (200):
 * { "result": 0, "message": "Specialist referral created successfully.", "specialistReferralId": 45 }
 *
 * Posibles errores (400):
 * { "result": 1, "message": "CIP_ID is required." }
 * { "result": 2, "message": "Patient not found." }
 * { "result": 3, "message": "Specialist type not found or inactive." }
 * { "result": 4, "message": "Specialist not found or inactive in directory." }
 * { "result": 5, "message": "Care Manager is required when 'Needs Care Manager' is enabled." }
 * { "result": 6, "message": "At least one primary diagnosis is required." }
 */
export const createReferral = async (data) => {
  try {
    const response = await axios.post(API_URL, data, authHeader());
    return response.data;
  } catch (error) {
    logEndpointError("createReferral", "POST", API_URL, error);
    throw error;
  }
};

/**
 * Actualiza un Specialist Referral existente. El SP compara campo a campo y
 * registra en log HIPAA solo los campos que realmente cambiaron.
 *
 * Cambio 1: el body incluye la lista completa `diagnoses`. El backend la
 * **reconcilia** contra el estado en BD dentro de una transacción única
 * (inserta nuevos, elimina los que ya no están, actualiza categoría primario↔asociado).
 *
 * @param {Object} data - Datos del referral (mismos campos que create + specialistReferralId)
 * @param {number} data.specialistReferralId - ID del referral a actualizar — requerido
 * @param {Array<{codeDiagnosis: string, isPrimary: boolean}>} data.diagnoses - Lista
 *        completa de diagnósticos (mínimo 1 primario). Ver createReferral.
 *
 * Ejemplo de llamada:
 * PUT /api/SpecialistReferrals
 * Body: { "specialistReferralId": 45, "urgencyLevelId": 9, "diagnoses": [...], ... }
 *
 * Respuesta exitosa (200):
 * { "result": 0, "message": "Specialist referral updated successfully." }
 *
 * Posibles errores (400):
 * { "result": 1, "message": "Referral not found or inactive." }
 * { "result": 2, "message": "Specialist type not found or inactive." }
 * { "result": 3, "message": "Specialist not found or inactive in directory." }
 * { "result": 4, "message": "Care Manager is required when 'Needs Care Manager' is enabled." }
 * { "result": 6, "message": "At least one primary diagnosis is required." }
 */
export const updateReferral = async (data) => {
  try {
    const response = await axios.put(API_URL, data, authHeader());
    return response.data;
  } catch (error) {
    logEndpointError("updateReferral", "PUT", API_URL, error);
    throw error;
  }
};

/**
 * Soft delete de un Specialist Referral (NUM_STATUS de 1 a 0).
 * No elimina el Lab Phlebotomy asociado.
 *
 * @param {number} id - ID del referral a eliminar
 *
 * Ejemplo de llamada:
 * DELETE /api/SpecialistReferrals/45
 *
 * Respuesta exitosa (200):
 * { "result": 0, "message": "Specialist referral deleted successfully." }
 *
 * Posibles errores (400):
 * { "result": 1, "message": "Referral not found or already inactive." }
 */
export const deleteReferral = async (id) => {
  const url = `${API_URL}/${id}`;
  try {
    const response = await axios.delete(url, authHeader());
    return response.data;
  } catch (error) {
    logEndpointError("deleteReferral", "DELETE", url, error);
    throw error;
  }
};

/**
 * Retorna la lista paginada de referrals activos con filtros opcionales.
 *
 * @param {Object} [filters]
 * @param {string} [filters.cipId] - Filtrar por paciente
 * @param {number} [filters.specialistTypeId] - Filtrar por tipo de especialista
 * @param {number} [filters.referralStatusId] - Filtrar por estado del referral (10-14)
 * @param {number} [filters.followupStatusId] - Filtrar por estado de seguimiento (15-17)
 * @param {number} [filters.locationId] - Filtrar por centro médico
 * @param {number} [filters.pcpId] - Filtrar por PCP
 * @param {number} [filters.urgencyLevelId] - Filtrar por urgencia (7-9)
 * @param {number} [filters.numEmployeeCreate] - Cambio 2. Filtrar por empleado que creó el referral
 * @param {number} [filters.medicalDirectorStatusId] - Cambio 2. Filtrar por Medical Director Status
 * @param {number} [filters.referralRequestId] - Cambio 2. Filtrar por tipo de Referral Request
 * @param {number} [filters.patientCodeStatusId] - Cambio 7. Filtrar por Patient Code Status (CPT): 100=New Patient, 101=Existing Patient
 * @param {string} [filters.dateFrom] - Desde fecha de creación (YYYY-MM-DD)
 * @param {string} [filters.dateTo] - Hasta fecha de creación (YYYY-MM-DD)
 * @param {number} [filters.page=1] - Página actual
 * @param {number} [filters.pageSize=20] - Registros por página
 *
 * Ejemplo de llamada:
 * GET /api/SpecialistReferrals/list?locationId=1&urgencyLevelId=9&page=1&pageSize=20
 *
 * Respuesta exitosa (200):
 * {
 *   "total": 45,
 *   "page": 1,
 *   "pageSize": 20,
 *   "totalPages": 3,
 *   "data": [
 *     {
 *       "specialistReferralId": 45,
 *       "cipId": "CIP000001",
 *       "memberId": "MEM12345",
 *       "createDate": "2026-02-28T10:30:00",
 *       "patientName": "John Doe",
 *       "patientDob": "1985-03-15T00:00:00",
 *       "specialistTypeId": 1,
 *       "specialistTypeName": "Cardiology",
 *       "specialistName": "Dr. John Smith",
 *       "specialistNpi": "1234567890",
 *       "specialistPhone": "(239) 555-0100",
 *       "urgencyLevelId": 7,
 *       "urgencyName": "Low Risk",
 *       "referralStatusId": 10,
 *       "referralStatusName": "Referral faxed to specialist",
 *       "followupStatusId": 15,
 *       "followupStatusName": "Pending",
 *       "followupNotes": "",
 *       "pcpId": 1,
 *       "pcpName": "Dr. Maria Martinez",
 *       "careManagerId": null,
 *       "careManagerName": "",
 *       "locationId": 1,
 *       "medicalCenter": "Fort Myers",
 *       "employeeStaffId": 1,
 *       "staffName": "Ana Lopez",
 *       "createdBy": "Ana Lopez",
 *       "medicalDirectorStatusName": "Approved",
 *       "referralRequestName": "Specialist",
 *       "patientCodeStatusId": 100,
 *       "patientCodeStatusName": "99205 - New Patient",
 *       "icd10Codes": "E11.65,I10",
 *       "reasonForReferral": "Patient needs cardiology evaluation",
 *       "hedisCodes": "CBP",
 *       "hasRubiconBeenUsed": false,
 *       "lab": true,
 *       "imaging": false,
 *       "availityReferral": "2026-02-28T00:00:00"
 *     }
 *   ]
 * }
 */
export const getReferralsList = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.cipId) params.append("cipId", filters.cipId);
  if (filters.specialistTypeId != null) params.append("specialistTypeId", filters.specialistTypeId);
  if (filters.referralStatusId != null) params.append("referralStatusId", filters.referralStatusId);
  if (filters.followupStatusId != null) params.append("followupStatusId", filters.followupStatusId);
  if (filters.locationId != null) params.append("locationId", filters.locationId);
  if (filters.pcpId != null) params.append("pcpId", filters.pcpId);
  if (filters.urgencyLevelId != null) params.append("urgencyLevelId", filters.urgencyLevelId);
  // El backend espera el query param `EmployeeCreate` (no `numEmployeeCreate`).
  // Enviarlo con el nombre equivocado hacía que el filtro "Created By" se
  // ignorara silenciosamente (el endpoint devolvía todo sin filtrar).
  if (filters.numEmployeeCreate != null) params.append("EmployeeCreate", filters.numEmployeeCreate);
  if (filters.medicalDirectorStatusId != null) params.append("medicalDirectorStatusId", filters.medicalDirectorStatusId);
  if (filters.referralRequestId != null) params.append("referralRequestId", filters.referralRequestId);
  if (filters.patientCodeStatusId != null) params.append("patientCodeStatusId", filters.patientCodeStatusId);
  if (filters.dateFrom) params.append("dateFrom", filters.dateFrom);
  if (filters.dateTo) params.append("dateTo", filters.dateTo);
  if (filters.page != null) params.append("page", filters.page);
  if (filters.pageSize != null) params.append("pageSize", filters.pageSize);

  const url = `${API_URL}/list?${params.toString()}`;
  try {
    const response = await axios.get(url, authHeader());
    return response.data;
  } catch (error) {
    logEndpointError("getReferralsList", "GET", url, error);
    throw error;
  }
};

/**
 * Retorna el detalle completo de un referral por su ID. Incluye datos del paciente,
 * nombres resueltos, campos desnormalizados del especialista, y quién creó/actualizó.
 *
 * @param {number} id - ID del referral
 *
 * Ejemplo de llamada:
 * GET /api/SpecialistReferrals/45
 *
 * Respuesta exitosa (200):
 * {
 *   "specialistReferralId": 45,
 *   "cipId": "CIP000001",
 *   "memberId": "MEM12345",
 *   "createDate": "2026-02-28T10:30:00",
 *   "updateDate": null,
 *   "status": 1,
 *   "patientName": "John Doe",
 *   "patientFirstName": "John",
 *   "patientLastName": "Doe",
 *   "patientDob": "1985-03-15T00:00:00",
 *   "patientAge": 41,
 *   "patientGender": "M",
 *   "patientPhone": "(239) 555-9999",
 *   "specialistTypeId": 1,
 *   "specialistTypeName": "Cardiology",
 *   "specialistCategory": "Internal Medicine",
 *   "specialistId": 1,
 *   "specialistName": "Dr. John Smith",
 *   "specialistNpi": "1234567890",
 *   "specialistPhone": "(239) 555-0100",
 *   "specialistFax": "(239) 555-0101",
 *   "specialistAddress": "1500 Royal Palm Sq Blvd, Fort Myers, FL 33919",
 *   "urgencyLevelId": 7,
 *   "urgencyName": "Low Risk",
 *   "referralStatusId": 10,
 *   "referralStatusName": "Referral faxed to specialist",
 *   "followupStatusId": 15,
 *   "followupStatusName": "Pending",
 *   "followupNotes": "",
 *   "icd10Codes": "E11.65,I10",
 *   "reasonForReferral": "Patient needs cardiology evaluation",
 *   "hedisCodes": "CBP",
 *   "hasRubiconBeenUsed": false,
 *   "lab": true,
 *   "imaging": false,
 *   "pcpId": 1,
 *   "pcpName": "Dr. Maria Martinez",
 *   "careManagerId": null,
 *   "careManagerName": "",
 *   "locationId": 1,
 *   "medicalCenter": "Fort Myers",
 *   "employeeStaffId": 1,
 *   "staffName": "Ana Lopez",
 *   "availityReferral": "2026-02-28T00:00:00",
 *   "employeeCreate": 1,
 *   "createdBy": "Ana Lopez",
 *   "employeeUpdate": null,
 *   "updatedBy": "",
 *   "needsCareManager": true,
 *   "referralRequestId": 41,
 *   "referralRequestName": "Specialist",
 *   "patientCodeStatusId": 100,
 *   "patientCodeStatusName": "99205 - New Patient",
 *   "medicalDirectorStatusId": 53,
 *   "medicalDirectorStatusName": "Approved",
 *   "medicalDirectorFollowupId": 57,
 *   "medicalDirectorFollowupName": "Labs needed",
 *   "directorReviewed": "2026-05-19T00:00:00",
 *   "medicalDirectorId": 2,
 *   "medicalDirectorName": "Reynaldo Pezzotti",
 *   "medicalDirectorEmail": "Drpezzottismith@gmail.com",
 *   "diagnoses": [
 *     {
 *       "referralDiagnosisId": 1,
 *       "specialistReferralId": 45,
 *       "codeDiagnosis": "E11.9",
 *       "isPrimary": true,
 *       "description": "Type 2 diabetes mellitus without complications",
 *       "createDate": "2026-05-20T10:23:11",
 *       "updateDate": null
 *     }
 *   ]
 * }
 *
 * Error (404):
 * { "message": "Referral not found." }
 */
export const getReferralById = async (id) => {
  const url = `${API_URL}/${id}`;
  try {
    const response = await axios.get(url, authHeader());
    return response.data;
  } catch (error) {
    logEndpointError("getReferralById", "GET", url, error);
    throw error;
  }
};

/**
 * Retorna todos los referrals activos de un paciente. Sin paginación.
 * Se usa desde Patient Map → "View Referrals".
 *
 * @param {string} cipId - CIP ID del paciente
 *
 * Ejemplo de llamada:
 * GET /api/SpecialistReferrals/by-patient/CIP000001
 *
 * Respuesta exitosa (200):
 * [
 *   {
 *     "specialistReferralId": 45,
 *     "cipId": "CIP000001",
 *     "memberId": "MEM12345",
 *     "createDate": "2026-02-28T10:30:00",
 *     "specialistTypeId": 1,
 *     "specialistTypeName": "Cardiology",
 *     "specialistName": "Dr. John Smith",
 *     "urgencyLevelId": 9,
 *     "urgencyName": "Stat / High Risk",
 *     "referralStatusId": 12,
 *     "referralStatusName": "Called PT about referral",
 *     "followupStatusId": 16,
 *     "followupStatusName": "Working",
 *     "pcpId": 1,
 *     "pcpName": "Dr. Maria Martinez",
 *     "locationId": 1,
 *     "medicalCenter": "Fort Myers",
 *     "icd10Codes": "E11.65,I10",
 *     "reasonForReferral": "Patient needs cardiology evaluation"
 *   }
 * ]
 *
 * Sin resultados: [] (200 OK)
 */
export const getReferralsByPatient = async (cipId) => {
  const url = `${API_URL}/by-patient/${cipId}`;
  try {
    const response = await axios.get(url, authHeader());
    return response.data;
  } catch (error) {
    logEndpointError("getReferralsByPatient", "GET", url, error);
    throw error;
  }
};

// ────────────────────────────────────────────────────────────
// GRUPO B: Comentarios (4 funciones)
// ────────────────────────────────────────────────────────────

/**
 * Obtiene todos los comentarios activos de un referral, ordenados por fecha
 * de creación descendente (más recientes primero).
 *
 * @param {number} referralId - ID del Specialist Referral
 *
 * Ejemplo de llamada:
 * GET /api/SpecialistReferrals/1/comments
 *
 * Respuesta exitosa (200):
 * [
 *   {
 *     "commentId": 3,
 *     "specialistReferralId": 1,
 *     "comment": "Patient confirmed appointment for 03/15/2026.",
 *     "createDate": "2026-02-28T15:30:00",
 *     "employeeCreate": 5,
 *     "createdBy": "Maria Garcia",
 *     "updateDate": null,
 *     "employeeUpdate": null,
 *     "updatedBy": ""
 *   }
 * ]
 *
 * Sin comentarios o referral no encontrado: [] (200 OK)
 */
export const getCommentsByReferral = async (referralId) => {
  const url = `${API_URL}/${referralId}/comments`;
  try {
    const response = await axios.get(url, authHeader());
    return response.data;
  } catch (error) {
    logEndpointError("getCommentsByReferral", "GET", url, error);
    throw error;
  }
};

/**
 * Crea un comentario en un referral. Máximo 2000 caracteres.
 *
 * @param {Object} data
 * @param {number} data.specialistReferralId - ID del referral al que pertenece
 * @param {string} data.comment - Texto del comentario (max 2000 chars)
 *
 * Ejemplo de llamada:
 * POST /api/SpecialistReferrals/comments
 * Body: { "specialistReferralId": 1, "comment": "Contacted specialist office." }
 *
 * Respuesta exitosa (200):
 * { "result": 0, "message": "Comment created successfully.", "commentId": 4 }
 *
 * Posibles errores (400):
 * { "result": 1, "message": "Referral not found or inactive." }
 * { "result": 2, "message": "Comment text is required." }
 */
export const createComment = async (data) => {
  const url = `${API_URL}/comments`;
  try {
    const response = await axios.post(url, data, authHeader());
    return response.data;
  } catch (error) {
    logEndpointError("createComment", "POST", url, error);
    throw error;
  }
};

/**
 * Actualiza el texto de un comentario existente. Máximo 2000 caracteres.
 *
 * @param {Object} data
 * @param {number} data.commentId - ID del comentario a actualizar
 * @param {string} data.comment - Nuevo texto del comentario (max 2000 chars)
 *
 * Ejemplo de llamada:
 * PUT /api/SpecialistReferrals/comments
 * Body: { "commentId": 1, "comment": "Texto actualizado." }
 *
 * Respuesta exitosa (200):
 * { "result": 0, "message": "Comment updated successfully." }
 *
 * Posibles errores (400):
 * { "result": 1, "message": "Comment not found or inactive." }
 * { "result": 2, "message": "Comment text is required." }
 */
export const updateComment = async (data) => {
  const url = `${API_URL}/comments`;
  try {
    const response = await axios.put(url, data, authHeader());
    return response.data;
  } catch (error) {
    logEndpointError("updateComment", "PUT", url, error);
    throw error;
  }
};

/**
 * Soft delete de un comentario (NUM_STATUS de 1 a 0).
 *
 * @param {number} commentId - ID del comentario a eliminar
 *
 * Ejemplo de llamada:
 * DELETE /api/SpecialistReferrals/comments/1
 *
 * Respuesta exitosa (200):
 * { "result": 0, "message": "Comment deleted successfully." }
 *
 * Posibles errores (400):
 * { "result": 1, "message": "Comment not found or already inactive." }
 */
export const deleteComment = async (commentId) => {
  const url = `${API_URL}/comments/${commentId}`;
  try {
    const response = await axios.delete(url, authHeader());
    return response.data;
  } catch (error) {
    logEndpointError("deleteComment", "DELETE", url, error);
    throw error;
  }
};

// ────────────────────────────────────────────────────────────
// GRUPO C: Catálogos y Directorio (3 funciones)
// ────────────────────────────────────────────────────────────

/**
 * Retorna los tipos de especialista activos para popular el dropdown del formulario.
 * Filtro opcional por categoría.
 *
 * @param {string} [category] - Filtro por categoría (ej: "Internal Medicine", "Surgical")
 *
 * Ejemplo de llamada:
 * GET /api/SpecialistReferrals/specialist-types
 * GET /api/SpecialistReferrals/specialist-types?category=Surgical
 *
 * Respuesta exitosa (200):
 * [
 *   {
 *     "specialistTypeId": 1,
 *     "specialistTypeName": "Cardiologist",
 *     "description": "Heart and cardiovascular system specialist",
 *     "category": "Internal Medicine",
 *     "displayOrder": 1
 *   }
 * ]
 */
export const getSpecialistTypes = async (category) => {
  const params = new URLSearchParams();
  if (category) params.append("category", category);
  const query = params.toString();
  const url = `${API_URL}/specialist-types${query ? `?${query}` : ""}`;
  try {
    const response = await axios.get(url, authHeader());
    return response.data;
  } catch (error) {
    logEndpointError("getSpecialistTypes", "GET", url, error);
    throw error;
  }
};

/**
 * Inserta un nuevo especialista en el directorio. Se usa desde el botón
 * "Add New Specialist" cuando el especialista no existe.
 *
 * @param {Object} data
 * @param {string} data.specialistName - Nombre del especialista — requerido
 * @param {number} data.specialistTypeId - FK a TAB_SPECIALIST_TYPES — requerido
 * @param {string} [data.npi] - National Provider Identifier
 * @param {string} [data.phone] - Teléfono
 * @param {string} [data.fax] - Fax
 * @param {string} [data.email] - Email
 * @param {string} [data.address] - Dirección
 * @param {string} [data.city] - Ciudad
 * @param {string} [data.state] - Estado (default "FL" en SP)
 * @param {string} [data.zipCode] - Código postal
 * @param {string} [data.region] - Región
 * @param {string} [data.specialistCategory] - "Individual" o "Group"
 * @param {string} [data.notes] - Notas opcionales
 *
 * Ejemplo de llamada:
 * POST /api/SpecialistReferrals/specialist-directory
 * Body: { "specialistName": "Dr. Roberto Fernandez", "specialistTypeId": 1, "npi": "1234567890", ... }
 *
 * Respuesta exitosa (200):
 * { "result": 0, "message": "Specialist added to directory successfully.", "specialistId": 42 }
 *
 * Posibles errores (400):
 * { "result": 1, "message": "Specialist name is required." }
 * { "result": 2, "message": "Specialist type not found or inactive." }
 * { "result": 3, "message": "A specialist with this NPI already exists in the directory." }
 */
export const addSpecialistToDirectory = async (data) => {
  const url = `${API_URL}/specialist-directory`;
  try {
    const response = await axios.post(url, data, authHeader());
    return response.data;
  } catch (error) {
    logEndpointError("addSpecialistToDirectory", "POST", url, error);
    throw error;
  }
};

/**
 * Retorna los Care Managers activos. Filtro opcional por location.
 * Se usa para popular el dropdown de Care Manager en el formulario.
 *
 * @param {number} [locationId] - Filtro por location. NULL = retorna todos
 *
 * Ejemplo de llamada:
 * GET /api/SpecialistReferrals/care-managers
 * GET /api/SpecialistReferrals/care-managers?locationId=5
 *
 * Respuesta exitosa (200):
 * [
 *   {
 *     "employeeId": 101,
 *     "firstName": "Maria",
 *     "lastName": "Lopez",
 *     "fullName": "Maria Lopez",
 *     "title": "Care Manager",
 *     "locationId": 5,
 *     "medicalCenter": "Naples Main Clinic"
 *   }
 * ]
 *
 * Sin resultados: [] (200 OK)
 */
export const getCareManagers = async (locationId) => {
  const params = new URLSearchParams();
  if (locationId != null) params.append("locationId", locationId);
  const query = params.toString();
  const url = `${API_URL}/care-managers${query ? `?${query}` : ""}`;
  try {
    const response = await axios.get(url, authHeader());
    return response.data;
  } catch (error) {
    logEndpointError("getCareManagers", "GET", url, error);
    throw error;
  }
};

// ────────────────────────────────────────────────────────────
// GRUPO D: Search / Autocomplete (5 funciones)
// ────────────────────────────────────────────────────────────

/**
 * Busca especialistas en el directorio por nombre, NPI o ciudad.
 * Los más utilizados aparecen primero (timesUsed). Retorna TOP 20.
 *
 * @param {string} searchTerm - Nombre, NPI o ciudad del especialista
 * @param {number} [specialistTypeId] - Filtro por tipo de especialista
 * @returns {Promise<Array>} Array de especialistas o [] si searchTerm es falsy
 *
 * Ejemplo de llamada:
 * GET /api/SpecialistReferrals/search/specialists-directory?searchTerm=Fernandez
 * GET /api/SpecialistReferrals/search/specialists-directory?searchTerm=Naples&specialistTypeId=1
 *
 * Respuesta exitosa (200):
 * [
 *   {
 *     "specialistId": 42,
 *     "specialistName": "Dr. Roberto Fernandez",
 *     "specialistTypeId": 1,
 *     "specialistTypeName": "Cardiologist",
 *     "npi": "1234567890",
 *     "phone": "(239) 555-0303",
 *     "fax": "(239) 555-0304",
 *     "email": "rfernandez@clinic.com",
 *     "address": "5678 Health Blvd",
 *     "city": "Naples",
 *     "state": "FL",
 *     "zipCode": "34102",
 *     "region": "Naples",
 *     "specialistCategory": "Individual",
 *     "notes": "Accepts Medicare patients",
 *     "timesUsed": 15
 *   }
 * ]
 */
export const searchSpecialistsDirectory = async (searchTerm, specialistTypeId) => {
  if (!searchTerm) return [];
  const params = new URLSearchParams();
  params.append("searchTerm", searchTerm);
  if (specialistTypeId != null) params.append("specialistTypeId", specialistTypeId);
  const url = `${API_URL}/search/specialists-directory?${params.toString()}`;
  try {
    const response = await axios.get(url, authHeader());
    return response.data;
  } catch (error) {
    logEndpointError("searchSpecialistsDirectory", "GET", url, error);
    throw error;
  }
};

/**
 * Busca diagnósticos ICD-10 por código o descripción. Retorna TOP 20.
 *
 * @param {string} searchTerm - Código ICD-10 o descripción a buscar
 * @returns {Promise<Array>} Array de diagnósticos o [] si searchTerm es falsy
 *
 * Ejemplo de llamada:
 * GET /api/SpecialistReferrals/search/diagnoses?searchTerm=E11
 * GET /api/SpecialistReferrals/search/diagnoses?searchTerm=diabetes
 *
 * Respuesta exitosa (200):
 * [
 *   {
 *     "code": "E11.0",
 *     "description": "Type 2 diabetes mellitus with hyperosmolarity"
 *   },
 *   {
 *     "code": "E11.21",
 *     "description": "Type 2 diabetes mellitus with diabetic nephropathy"
 *   }
 * ]
 */
export const searchDiagnoses = async (searchTerm) => {
  if (!searchTerm) return [];
  const url = `${API_URL}/search/diagnoses?searchTerm=${encodeURIComponent(searchTerm)}`;
  try {
    const response = await axios.get(url, authHeader());
    return response.data;
  } catch (error) {
    logEndpointError("searchDiagnoses", "GET", url, error);
    throw error;
  }
};

/**
 * Busca códigos HEDIS por código o descripción. Retorna TOP 20.
 *
 * @param {string} searchTerm - Código HEDIS o descripción a buscar
 * @returns {Promise<Array>} Array de códigos HEDIS o [] si searchTerm es falsy
 *
 * Ejemplo de llamada:
 * GET /api/SpecialistReferrals/search/hedis-codes?searchTerm=BCS
 * GET /api/SpecialistReferrals/search/hedis-codes?searchTerm=breast
 *
 * Respuesta exitosa (200):
 * [
 *   {
 *     "hedisCodeId": 1,
 *     "hedisCode": "BCS",
 *     "hedisDescription": "Breast Cancer Screening"
 *   }
 * ]
 */
export const searchHedisCodes = async (searchTerm) => {
  if (!searchTerm) return [];
  const url = `${API_URL}/search/hedis-codes?searchTerm=${encodeURIComponent(searchTerm)}`;
  try {
    const response = await axios.get(url, authHeader());
    return response.data;
  } catch (error) {
    logEndpointError("searchHedisCodes", "GET", url, error);
    throw error;
  }
};

/**
 * Autocomplete de pacientes por CIP_ID o nombre. Retorna TOP 20 pacientes activos.
 * Registra log HIPAA automáticamente.
 *
 * @param {string} searchTerm - CIP_ID, first name, last name o full name
 * @param {number} [locationId] - Filtro opcional por location
 * @returns {Promise<Array>} Array de pacientes o [] si searchTerm es falsy
 *
 * Ejemplo de llamada:
 * GET /api/SpecialistReferrals/search/patients?searchTerm=Smith
 * GET /api/SpecialistReferrals/search/patients?searchTerm=CIP123&locationId=5
 *
 * Respuesta exitosa (200):
 * [
 *   {
 *     "cipId": "CIP123456789",
 *     "memberFullName": "John Smith",
 *     "firstName": "John",
 *     "lastName": "Smith",
 *     "dob": "1985-03-15T00:00:00",
 *     "age": 41,
 *     "gender": "M",
 *     "memberPhoneNumber": "(239) 555-0101",
 *     "status": 1
 *   }
 * ]
 */
export const searchPatients = async (searchTerm, locationId) => {
  if (!searchTerm) return [];
  const params = new URLSearchParams();
  params.append("searchTerm", searchTerm);
  if (locationId != null) params.append("locationId", locationId);
  const url = `${API_URL}/search/patients?${params.toString()}`;
  try {
    const response = await axios.get(url, authHeader());
    return response.data;
  } catch (error) {
    logEndpointError("searchPatients", "GET", url, error);
    throw error;
  }
};

/**
 * Busca razones de referral existentes. Las razones más usadas aparecen primero
 * (timesUsed). La tabla crece orgánicamente cada vez que se crea un referral
 * con una razón nueva. Retorna TOP 20.
 *
 * @param {string} searchTerm - Texto de la razón a buscar
 * @returns {Promise<Array>} Array de razones o [] si searchTerm es falsy
 *
 * Ejemplo de llamada:
 * GET /api/SpecialistReferrals/search/referral-reasons?searchTerm=follow
 *
 * Respuesta exitosa (200):
 * [
 *   {
 *     "referralReasonId": 5,
 *     "referralReason": "Follow-up after hospitalization",
 *     "timesUsed": 120
 *   },
 *   {
 *     "referralReasonId": 12,
 *     "referralReason": "Follow-up cardiac evaluation",
 *     "timesUsed": 85
 *   }
 * ]
 */
export const searchReferralReasons = async (searchTerm) => {
  if (!searchTerm) return [];
  const url = `${API_URL}/search/referral-reasons?searchTerm=${encodeURIComponent(searchTerm)}`;
  try {
    const response = await axios.get(url, authHeader());
    return response.data;
  } catch (error) {
    logEndpointError("searchReferralReasons", "GET", url, error);
    throw error;
  }
};

// ────────────────────────────────────────────────────────────
// GRUPO E: Patient Data y Reasons (3 funciones)
// ────────────────────────────────────────────────────────────

/**
 * Obtiene información demográfica completa de un paciente por CIP_ID.
 * Incluye datos personales, contacto, PCP asignado y location.
 * Registra log HIPAA automáticamente.
 *
 * @param {string} cipId - ID único del paciente
 *
 * Ejemplo de llamada:
 * GET /api/SpecialistReferrals/patient-demographics/CIP123456789
 *
 * Respuesta exitosa (200):
 * {
 *   "cipId": "CIP123456789",
 *   "memberFullName": "John Michael Smith",
 *   "firstName": "John",
 *   "lastName": "Smith",
 *   "middleName": "Michael",
 *   "dob": "1985-03-15T00:00:00",
 *   "age": 41,
 *   "gender": "M",
 *   "memberEmail": "john.smith@email.com",
 *   "memberPhoneNumber": "(239) 555-0101",
 *   "memberMobileNumber": "(239) 555-0202",
 *   "timeLastVisit": "2026-01-10",
 *   "status": 1,
 *   "pcpEmployeeId": 50,
 *   "pcpFirstName": "Ana",
 *   "pcpLastName": "Garcia",
 *   "pcpFullName": "Ana Garcia",
 *   "pcpTitle": "MD",
 *   "assignmentSource": "Auto-Assignment",
 *   "crossLocation": false,
 *   "locationId": 3,
 *   "medicalCenter": "Fort Myers Health Center",
 *   "addressLine1": "1234 Palm Ave",
 *   "addressLine2": "Suite 100",
 *   "cityName": "Fort Myers",
 *   "state": "FL",
 *   "zipCode": "33901",
 *   "county": "Lee"
 * }
 *
 * Error (404):
 * { "message": "Patient not found." }
 */
export const getPatientDemographics = async (cipId) => {
  const url = `${API_URL}/patient-demographics/${cipId}`;
  try {
    const response = await axios.get(url, authHeader());
    return response.data;
  } catch (error) {
    logEndpointError("getPatientDemographics", "GET", url, error);
    throw error;
  }
};

/**
 * Obtiene todas las membresías (pólizas de seguro) asociadas a un paciente.
 * Un paciente puede tener membresías ACA y/o MED con múltiples Member_IDs.
 * Registra log HIPAA.
 *
 * @param {string} cipId - ID único del paciente
 *
 * Ejemplo de llamada:
 * GET /api/SpecialistReferrals/patient-memberships/CIP123456789
 *
 * Respuesta exitosa (200):
 * [
 *   {
 *     "memberType": "ACA",
 *     "cipId": "CIP123456789",
 *     "memberId": "ACA-2024-00123",
 *     "anPrefix": "A",
 *     "memberPolicyId": "POL-5678",
 *     "networkCode": "BLUE01",
 *     "networkName": "Blue Cross PPO",
 *     "metalLevel": "Gold",
 *     "memberPlan": "Gold PPO 1500",
 *     "memberPlanEffectiveDate": "2026-01-01T00:00:00",
 *     "groupNumber": "GRP-1001",
 *     "valueBasedName": "Value Care Plus",
 *     "awvYtd": "",
 *     "status": 1,
 *     "createDate": "2025-12-15T10:30:00",
 *     "updateDate": "2026-01-02T08:00:00"
 *   }
 * ]
 *
 * Sin membresías: [] (200 OK)
 */
export const getPatientMemberships = async (cipId) => {
  const url = `${API_URL}/patient-memberships/${cipId}`;
  try {
    const response = await axios.get(url, authHeader());
    return response.data;
  } catch (error) {
    logEndpointError("getPatientMemberships", "GET", url, error);
    throw error;
  }
};

/**
 * Crea una nueva razón de referral. Se usa cuando el autocomplete no encuentra
 * coincidencia y el usuario confirma "Add new reason".
 *
 * @param {Object} data
 * @param {string} data.referralReason - Texto de la nueva razón
 *
 * Ejemplo de llamada:
 * POST /api/SpecialistReferrals/referral-reasons
 * Body: { "referralReason": "cardiac evaluation" }
 *
 * Respuesta exitosa (200):
 * { "result": 0, "referralReasonId": 47 }
 */
export const createReferralReason = async (data) => {
  const url = `${API_URL}/referral-reasons`;
  try {
    const response = await axios.post(url, data, authHeader());
    return response.data;
  } catch (error) {
    logEndpointError("createReferralReason", "POST", url, error);
    throw error;
  }
};

// ────────────────────────────────────────────────────────────
// GRUPO F: Diagnósticos del Referral (Cambio 1 — 4 funciones)
// ────────────────────────────────────────────────────────────
//
// NOTA: el formulario de crear/editar NO usa estos endpoints atómicos —
// envía la lista completa `diagnoses` en POST/PUT del referral y el backend
// la orquesta en transacción. Estas funciones quedan disponibles para una
// futura edición rápida de diagnósticos (ej. desde el listado o un detalle).

/**
 * Lista los diagnósticos activos de un referral (primarios primero).
 *
 * @param {number} referralId - ID del Specialist Referral
 *
 * Ejemplo de llamada:
 * GET /api/SpecialistReferrals/45/diagnoses
 *
 * Respuesta exitosa (200):
 * [
 *   {
 *     "referralDiagnosisId": 1,
 *     "specialistReferralId": 45,
 *     "codeDiagnosis": "E11.9",
 *     "isPrimary": true,
 *     "description": "Type 2 diabetes mellitus without complications",
 *     "createDate": "2026-05-19T10:23:11",
 *     "updateDate": null
 *   }
 * ]
 *
 * Error (404):
 * { "message": "Specialist referral not found." }
 */
export const getReferralDiagnoses = async (referralId) => {
  const url = `${API_URL}/${referralId}/diagnoses`;
  try {
    const response = await axios.get(url, authHeader());
    return response.data;
  } catch (error) {
    logEndpointError("getReferralDiagnoses", "GET", url, error);
    throw error;
  }
};

/**
 * Agrega un diagnóstico individual a un referral.
 *
 * @param {Object} data
 * @param {number} data.specialistReferralId - ID del referral
 * @param {string} data.codeDiagnosis - Código ICD-10 (debe existir en el catálogo)
 * @param {boolean} data.isPrimary - true = primario, false = asociado
 *
 * Ejemplo de llamada:
 * POST /api/SpecialistReferrals/diagnoses
 * Body: { "specialistReferralId": 45, "codeDiagnosis": "E11.9", "isPrimary": true }
 *
 * Respuesta exitosa (200):
 * { "result": 0, "message": "Diagnosis added to the referral successfully.", "referralDiagnosisId": 1 }
 *
 * Posibles errores (400):
 * { "result": 1, "message": "Referral not found or inactive." }
 * { "result": 2, "message": "ICD-10 code does not exist in the catalog." }
 * { "result": 3, "message": "Duplicate diagnosis (same code and category)." }
 * { "result": 4, "message": "Diagnosis already active with another category — use PUT." }
 */
export const addReferralDiagnosis = async (data) => {
  const url = `${API_URL}/diagnoses`;
  try {
    const response = await axios.post(url, data, authHeader());
    return response.data;
  } catch (error) {
    logEndpointError("addReferralDiagnosis", "POST", url, error);
    throw error;
  }
};

/**
 * Cambia la categoría primario↔asociado de un diagnóstico existente.
 *
 * @param {Object} data
 * @param {number} data.referralDiagnosisId - ID del diagnóstico del referral
 * @param {boolean} data.isPrimary - Nueva categoría
 *
 * Ejemplo de llamada:
 * PUT /api/SpecialistReferrals/diagnoses
 * Body: { "referralDiagnosisId": 1, "isPrimary": false }
 *
 * Respuesta exitosa (200):
 * { "result": 0, "message": "Referral diagnosis updated successfully." }
 * { "result": 0, "message": "No changes detected." }
 *
 * Posibles errores (400):
 * { "result": 1, "message": "Referral diagnosis not found or inactive." }
 */
export const updateReferralDiagnosis = async (data) => {
  const url = `${API_URL}/diagnoses`;
  try {
    const response = await axios.put(url, data, authHeader());
    return response.data;
  } catch (error) {
    logEndpointError("updateReferralDiagnosis", "PUT", url, error);
    throw error;
  }
};

/**
 * Soft delete de un diagnóstico del referral. Idempotente.
 *
 * @param {number} id - ID del diagnóstico del referral (referralDiagnosisId)
 *
 * Ejemplo de llamada:
 * DELETE /api/SpecialistReferrals/diagnoses/1
 *
 * Respuesta exitosa (200):
 * { "result": 0, "message": "Referral diagnosis deleted successfully." }
 * { "result": 0, "message": "Diagnosis was already inactive." }
 *
 * Posibles errores (400):
 * { "result": 1, "message": "Referral diagnosis not found." }
 */
export const deleteReferralDiagnosis = async (id) => {
  const url = `${API_URL}/diagnoses/${id}`;
  try {
    const response = await axios.delete(url, authHeader());
    return response.data;
  } catch (error) {
    logEndpointError("deleteReferralDiagnosis", "DELETE", url, error);
    throw error;
  }
};

// ────────────────────────────────────────────────────────────
// GRUPO G: Métricas del listado (Cambio 2 — 1 función)
// ────────────────────────────────────────────────────────────

/**
 * Retorna el conteo de referrals por centro médico. Se usa para el sidebar de
 * "Medical Centers" del listado e incluye centros con conteo 0.
 *
 * Reenvía los MISMOS filtros del listado (excepto `locationId`, que aquí no
 * aplica porque el conteo es por cada location) para que las cantidades del
 * sidebar reflejen los filtros activos. El query param de "creado por" se
 * llama `EmployeeCreate` (igual que en `/list`). El backend debe honrar estos
 * filtros; si los ignora, el conteo cae al global (sin regresión).
 *
 * @param {Object} [filters]
 * @param {number|string} [filters.urgencyLevelId]
 * @param {number|string} [filters.referralStatusId]
 * @param {number|string} [filters.followupStatusId]
 * @param {number|string} [filters.numEmployeeCreate]      - se envía como `EmployeeCreate`
 * @param {number|string} [filters.medicalDirectorStatusId]
 * @param {number|string} [filters.referralRequestId]
 *
 * Ejemplo de llamada:
 * GET /api/SpecialistReferrals/referral-counts-by-location?referralStatusId=10&EmployeeCreate=95
 *
 * Respuesta exitosa (200):
 * [
 *   { "locationId": 5, "medicalCenter": "Cape Coral Office", "referralCount": 0 },
 *   { "locationId": 2, "medicalCenter": "Fort Myers Office", "referralCount": 23 },
 *   { "locationId": 1, "medicalCenter": "Naples Office", "referralCount": 47 }
 * ]
 */
export const getReferralCountsByLocation = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.urgencyLevelId != null) params.append("urgencyLevelId", filters.urgencyLevelId);
  if (filters.referralStatusId != null) params.append("referralStatusId", filters.referralStatusId);
  if (filters.followupStatusId != null) params.append("followupStatusId", filters.followupStatusId);
  if (filters.numEmployeeCreate != null) params.append("EmployeeCreate", filters.numEmployeeCreate);
  if (filters.medicalDirectorStatusId != null) params.append("medicalDirectorStatusId", filters.medicalDirectorStatusId);
  if (filters.referralRequestId != null) params.append("referralRequestId", filters.referralRequestId);

  const qs = params.toString();
  const url = `${API_URL}/referral-counts-by-location${qs ? `?${qs}` : ""}`;
  try {
    const response = await axios.get(url, authHeader());
    return response.data;
  } catch (error) {
    logEndpointError("getReferralCountsByLocation", "GET", url, error);
    throw error;
  }
};
